// Landing pages · tipos y componentes compartidos (gráficos SVG, toggle de estado, miniatura).
import type { JSX } from "react";
import { useState } from "react";
import { Globe } from "lucide-react";

export interface LandingListItem {
  id: string; name: string; slug: string; status: "draft" | "published"; folder: string | null;
  visits: number; uniqueVisitors: number; conversions: number; conversionRate: number; source: string;
  createdAt: string; updatedAt: string; createdByName: string | null; thumb: string | null; publicUrl: string;
}

export type BlockType = "hero" | "text" | "columns" | "image" | "people" | "form" | "button" | "video" | "countdown" | "divider" | "share" | "footer" | "html";
export interface BlockStyle { bg?: string | undefined; color?: string | undefined; padding?: number | undefined; align?: "left" | "center" | "right" | undefined }
// Bloque genérico: los campos dependen del tipo (el servidor valida con zod estricto).
export interface Block {
  id: string; type: BlockType; style?: BlockStyle;
  [k: string]: unknown;
}
export interface Design {
  global?: { font?: string | undefined; primary?: string | undefined; maxWidth?: number | undefined; bg?: string | undefined; text?: string | undefined };
  blocks: Block[];
}
export interface Seo { title?: string; description?: string; image?: string; favicon?: string }

export interface LandingDetailData {
  id: string; name: string; slug: string; status: "draft" | "published"; folder: string | null;
  design: Design; html: string | null; seo: Seo; formId: string | null;
  visits: number; uniqueVisitors: number; conversions: number; source: string; externalUrl: string | null;
  createdAt: string; updatedAt: string; publicUrl: string; funnels: Array<{ id: string; name: string }>;
}

export const FONTS = ["Inter", "Roboto", "Open Sans", "Montserrat", "Poppins", "Lato", "Raleway", "Sistema"];

export const newId = (): string => Math.random().toString(36).slice(2, 10);

/** Interruptor publicada/borrador. */
export function StatusToggle({ published, onChange, disabled }: { published: boolean; onChange: (v: boolean) => void; disabled?: boolean }): JSX.Element {
  return (
    <label className={`inline-flex items-center gap-2 text-sm ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <button type="button" role="switch" aria-checked={published} disabled={disabled} onClick={() => onChange(!published)}
        className={`relative w-10 h-6 rounded-full transition-colors ${published ? "bg-success" : "bg-line-strong"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${published ? "left-[18px]" : "left-0.5"}`} />
      </button>
      <span className={published ? "text-success font-medium" : "text-ink-secondary"}>{published ? "Publicada" : "Borrador"}</span>
    </label>
  );
}

/** Donut de conversión (SVG). */
export function Donut({ value, total, label, size = 120 }: { value: number; total: number; label?: string; size?: number }): JSX.Element {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  const r = 42; const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={`${pct.toFixed(1)}% ${label ?? ""}`}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="var(--border)" strokeWidth="10" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="var(--primary)" strokeWidth="10" strokeLinecap="round"
        strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 50 50)" />
      <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fontSize="17" fontWeight="600" fill="var(--text)">{pct.toFixed(1)}%</text>
      {label && <text x="50" y="66" textAnchor="middle" fontSize="8" fill="var(--text-muted)">{label}</text>}
    </svg>
  );
}

/** Gráfico de líneas simple (SVG) con varias series sobre días. */
export function LineChart({ labels, series, height = 220 }: {
  labels: string[]; series: Array<{ name: string; color: string; values: number[] }>; height?: number;
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720; const H = height; const pl = 34; const pr = 10; const pt = 10; const pb = 26;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const nice = max <= 5 ? 5 : Math.ceil(max / 4) * 4;
  const x = (i: number): number => pl + (labels.length <= 1 ? (W - pl - pr) / 2 : (i * (W - pl - pr)) / (labels.length - 1));
  const y = (v: number): number => pt + (H - pt - pb) * (1 - v / nice);
  const step = Math.max(1, Math.ceil(labels.length / 8));
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Gráfico de visitas por día"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * W;
          const i = Math.round(((px - pl) / (W - pl - pr)) * (labels.length - 1));
          setHover(i >= 0 && i < labels.length ? i : null);
        }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={pl} x2={W - pr} y1={y(nice * f)} y2={y(nice * f)} stroke="var(--border)" strokeWidth="1" />
            <text x={pl - 6} y={y(nice * f)} textAnchor="end" dominantBaseline="central" fontSize="10" fill="var(--text-muted)">{Math.round(nice * f)}</text>
          </g>
        ))}
        {labels.map((l, i) => (i % step === 0 || i === labels.length - 1) && (
          <text key={l} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="var(--text-muted)">{l.slice(5).split("-").reverse().join("/")}</text>
        ))}
        {series.map((s) => (
          <g key={s.name}>
            <polyline fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ")} />
            {labels.length <= 31 && s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="2.5" fill={s.color} />)}
          </g>
        ))}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={pt} y2={H - pb} stroke="var(--border-strong)" strokeDasharray="3 3" />}
      </svg>
      {hover != null && (
        <div className="absolute top-1 right-2 card px-3 py-2 text-xs space-y-0.5 pointer-events-none">
          <div className="font-medium text-ink">{labels[hover]}</div>
          {series.map((s) => <div key={s.name} className="flex items-center gap-2 text-ink-secondary"><span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.name}: <span className="tnum text-ink">{s.values[hover]}</span></div>)}
        </div>
      )}
      <div className="flex flex-wrap gap-4 mt-1 text-xs text-ink-secondary">
        {series.map((s) => <span key={s.name} className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: s.color }} />{s.name}</span>)}
      </div>
    </div>
  );
}

/** Miniatura: primera imagen de la landing o un marcador. */
export function Thumb({ src, className }: { src: string | null; className?: string }): JSX.Element {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`overflow-hidden rounded-md border border-line bg-primary-soft grid place-items-center ${className ?? "w-20 h-14"}`}>
      {src && !failed
        ? <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" onError={() => setFailed(true)} />
        : <Globe size={20} strokeWidth={1.5} className="text-primary" />}
    </div>
  );
}

export const pct = (a: number, b: number): string => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : "0%");

export async function copyText(t: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(t); return true; } catch { return false; }
}
